import React, { useState } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { DollarSign, Settings, BarChart3 } from 'lucide-react';
import PageHeader from './ui/PageHeader';
import TransacoesPage from './TransacoesPage';
import CategoriasView from './financeiro/CategoriasView';
import ContasView from './financeiro/ContasView';
import DashboardFinanceiro from './financeiro/DashboardFinanceiro';

const GestaoFinanceira = ({ onLogout, isAdmin }) => {
  usePageTitle('Gestão Financeira');
  const { theme: themeColors, isDarkMode } = useTheme();
  const { sidebarWidth, isMobile } = useSidebar();
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'transacoes', 'categorias', 'contas'
  const [showConfigMenu, setShowConfigMenu] = useState(false);

  // Fechar menu de config ao pressionar ESC ou clicar fora
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showConfigMenu) {
        setShowConfigMenu(false);
      }
    };

    const handleClickOutside = (e) => {
      if (showConfigMenu) {
        const configButton = document.getElementById('config-button');
        const configMenu = document.getElementById('config-menu');
        
        if (configButton && configMenu && 
            !configButton.contains(e.target) && 
            !configMenu.contains(e.target)) {
          setShowConfigMenu(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showConfigMenu]);

  const renderView = () => {
    switch (currentView) {
      case 'dashboard':
        return <DashboardFinanceiro />;
      case 'categorias':
        return <CategoriasView onVoltar={() => setCurrentView('transacoes')} />;
      case 'contas':
        return <ContasView onVoltar={() => setCurrentView('transacoes')} />;
      default:
        return <TransacoesPage />;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: themeColors.bg }}>
      <Sidebar onLogout={onLogout} isAdmin={isAdmin} />
      <Header />
      
      {/* Main Content */}
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Header — padrão premium compartilhado */}
        <PageHeader
          icon={<DollarSign size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title="Gestão Financeira"
          description="Controle suas receitas e despesas em um único lugar"
        />

        {/* ABAS: Dashboard | Transações */}
        <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button
            onClick={() => setCurrentView('dashboard')}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: currentView === 'dashboard'
                ? 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)'
                : 'var(--dp-bg-secondary)',
              color: currentView === 'dashboard' ? 'white' : themeColors.text,
              fontFamily: 'var(--dp-font)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <BarChart3 size={18} />
            Dashboard
          </button>

          <button
            onClick={() => setCurrentView('transacoes')}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: currentView === 'transacoes'
                ? 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)'
                : 'var(--dp-bg-secondary)',
              color: currentView === 'transacoes' ? 'white' : themeColors.text,
              fontFamily: 'var(--dp-font)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <DollarSign size={18} />
            Transações
          </button>

          {/* Config Button - só aparece na aba Transações */}
          {currentView === 'transacoes' && (
            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button
                id="config-button"
                onClick={() => setShowConfigMenu(!showConfigMenu)}
                style={{
                  padding: '10px 20px',
                  background: isDarkMode ? '#252b3b' : 'white',
                  border: `2px solid ${themeColors.border}`,
                  borderRadius: '12px',
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = themeColors.accent;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = themeColors.border;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Settings size={18} />
                Configurações
              </button>

              {/* Config Menu Dropdown */}
              {showConfigMenu && (
                <div
                  id="config-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    background: isDarkMode ? '#252b3b' : 'white',
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '220px',
                    overflow: 'hidden'
                  }}
                >
                  <button
                    onClick={() => {
                      setCurrentView('categorias');
                      setShowConfigMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: 'transparent',
                      border: 'none',
                      color: themeColors.text,
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: '#8B5CF6'
                    }} />
                    Gerenciar Categorias
                  </button>

                  <button
                    onClick={() => {
                      setCurrentView('contas');
                      setShowConfigMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: 'transparent',
                      border: 'none',
                      borderTop: `1px solid ${themeColors.border}`,
                      color: themeColors.text,
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: '#3B82F6'
                    }} />
                    Gerenciar Contas
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Render Current View */}
        {renderView()}
      </main>
    </div>
  );
};

export default GestaoFinanceira;
